import _ from 'underscore';
import { parseSync, stringifySync } from '@bingads-webui/safe-json';
import * as downloadServiceSharedComponent from '@bingads-webui/download-service';
import {
  InlineDownloadReportDataRepository
} from '@bingads-webui-campaign/inline-download-report-data-repository';

import {
  NOT_SUPPORT_DOWNLOAD_TYPES,
  SUPPORTED_CAMPAIGN_TYPES,
} from '../consts';
import { getRecommendationTypes } from '../util';

/**
 * Service responsible for creating a download repository object and crafting the download request
 * to POST to odata.
 */
export class RecommendationDownloadService {
  constructor({
    scope,
    alertCenter,
    asyncScheduler,
    cacheManager,
    currentActivity,
    downloadService = downloadServiceSharedComponent,
    getODataErrors,
    i18n,
    odata,
    pageContext,
    permissions,
    isMCC = false,
    recommendationService = null,
  }) {
    this.repository = new InlineDownloadReportDataRepository({
      deps: {
        alertCenter,
        asyncScheduler,
        cacheManager,
        downloadService,
        getODataErrors,
        i18n,
        odata,
        pageContext,
      },
      isCustomerLevel: isMCC,
      currentActivity,
    });

    this.pageContext = pageContext;
    this.permissions = permissions;
    this.scope = scope;
    this.isMCC = isMCC;
    this.recommendationService = recommendationService;

    if (currentActivity) {
      this.currentActivity = currentActivity;
      this.currentActivity.trace('Initialized RecommendationDownloadService', 'RecommendationDownloadService/constructor');
    }
  }

  /**
   * Creates a download request and POSTS to odata, asynchronously polls for the status of the
   * request and initiates the browser download after the file has been generated.
   * @param {Object} [options.filterService] - Filter service library used to get combined filter.
   * @param {string} [options.recommendationType] - recommendation type
   * @param {string} [options.channel] - channel for competition or recommendation
   * @returns {Promise} Resolves to true if the download was successful.
   */
  download({
    filterService,
    recommendationType,
    campaignType,
    channel,
  }) {
    const ReportColumns = _.difference(
      getRecommendationTypes(
        recommendationType,
        channel,
        this.permissions,
        this.isMCC
      ),
      NOT_SUPPORT_DOWNLOAD_TYPES
    );

    const {
      CultureLcid,
      CurrentCustomer: {
        Id: CustomerId,
      },
      CurrentAccount: {
        Id: AccountId,
        AccountNumber,
      },
    } = this.pageContext;

    let { CurrentCampaign, CurrentAdGroup } = this.pageContext;
    CurrentCampaign = this.scope && this.scope.campaignId ? { Id: this.scope.campaignId } : null;
    CurrentAdGroup = this.scope && this.scope.adGroupId ?
      { Id: this.scope.adGroupId, CampaignId: this.scope.campaignId } : null;

    const query = filterService && filterService.getCombinedFilter().query;
    const CampaignReportScopes = this.getCampaignReportScopes(AccountId, CurrentCampaign, query);
    const AdGroupReportScopes = this.getAdGroupReportScopes(AccountId, CurrentAdGroup, query);

    if (this.isMCC && !_.isNull(this.recommendationService)) {
      return this.recommendationService.download(ReportColumns, channel);
    }
    return this.repository.download({
      Name: 'RecommendationsDownload',
      Type: 'RecommendationsDownload',
      ReportType: 'Recommendations',
      Channel: channel,
      CampaignTypeId: _.contains(SUPPORTED_CAMPAIGN_TYPES, campaignType) ? campaignType : null,
      AccountNumber,
      ReportColumns,
      Format: 'Xlsx',
      ReportTime: {
        DateRangePreset: 'AllTime',
        StartDate: null,
        EndDate: null,
      },
      CustomerId,
      AccountIds: [AccountId],
      CampaignReportScopes,
      AdGroupReportScopes,
      LCID: CultureLcid,
    }).catch((err) => {
      const errorDetails = `ErrorCode=${err.status}, Error=${stringifySync(err)}`;

      if (this.currentActivity) {
        this.currentActivity.error(`err in download, ${errorDetails}`, 'RecommendationDownloadService/Download');
      }
    });
  }

  /**
   * Creates an array of CampaignReportScope objects that specify the Campaigns to filter on.
   * @param {number} AccountId - Account Id.
   * @param {Object} CurrentCampaign - Current campaign scope.
   * @param {Object} query - Filter query that contains an array of JSON strings that describe the
   * campaigns to filter on.
   * @returns {Object[]} Campaigns to filter or empty array.
   */
  getCampaignReportScopes(AccountId, CurrentCampaign, query) {
    // if wunderbar scope has been set, ignore filter
    if (_.has(CurrentCampaign, 'Id')) {
      return [{ CampaignId: CurrentCampaign.Id, AccountId }];
    }

    const filteredCampaigns = query && query.Campaign && query.Campaign.$contains;

    return _.map(filteredCampaigns, (str) => {
      const campaign = parseSync(str);
      return campaign && {
        CampaignId: campaign.Id,
        AccountId,
      };
    });
  }

  /**
   * Creates an array of AdGroupReportScope objects that specify the ad groups to filter on.
   * @param {number} AccountId - Account Id.
   * @param {Object} CurrentAdGroup - Current ad group scope.
   * @param {Object} query - Filter query that contains an array of JSON strings that describe the
   * ad groups to filter on.
   * @returns {Object[]} Ad groups to filter or empty array.
   */
  getAdGroupReportScopes(AccountId, CurrentAdGroup, query) {
    // if wunderbar scope has been set, ignore filter
    if (_.has(CurrentAdGroup, 'Id') && _.has(CurrentAdGroup, 'CampaignId')) {
      return [{ AdGroupId: CurrentAdGroup.Id, CampaignId: CurrentAdGroup.CampaignId, AccountId }];
    }

    const filteredAdGroups = query && query.AdGroup && query.AdGroup.$contains;

    return _.map(filteredAdGroups, (str) => {
      const adGroup = parseSync(str);
      return adGroup && {
        AdGroupId: adGroup.Id,
        CampaignId: adGroup.CampaignId,
        AccountId,
      };
    });
  }
}
